import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/shared/ui/alert-dialog"

interface ConfirmationDialogProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title: string
    description: string
    confirmText?: string
    cancelText?: string
    variant?: "default" | "destructive"
    isLoading?: boolean
    /** Use when dialog must appear above sheets (e.g. z-[200]) */
    overlayClassName?: string
    contentClassName?: string
}

export function ConfirmationDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText,
    cancelText,
    variant = "default",
    isLoading = false,
    overlayClassName,
    contentClassName,
}: ConfirmationDialogProps) {
    const { t } = useTranslation('common');
    const displayConfirmText = confirmText ?? t('dialogs.confirmation.confirm');
    const displayCancelText = cancelText ?? t('dialogs.confirmation.cancel');

    const handleConfirmClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isLoading) {
            onConfirm();
        }
    };

    return (
        <AlertDialog open={isOpen} onOpenChange={(open) => !open && !isLoading && onClose()}>
            <AlertDialogContent ariaComplete className="sm:max-w-lg" contentClassName={contentClassName} overlayClassName={overlayClassName}>
                <AlertDialogHeader className="space-y-2">
                    <AlertDialogTitle className="text-lg font-semibold text-gray-900">
                        {title}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm text-gray-600">
                        {description}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <AlertDialogFooter className="gap-2 mt-6 flex-wrap">
                    <AlertDialogCancel
                        disabled={isLoading}
                        className="px-6 py-2.5 text-sm font-semibold text-primary bg-white border-2 border-primary rounded-lg hover:bg-primary/5 focus:outline-none focus-visible:outline-none !ring-0 transition-colors"
                    >
                        {displayCancelText}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleConfirmClick}
                        disabled={isLoading}
                        className={`px-6 py-2.5 text-sm font-semibold rounded-lg focus:outline-none focus-visible:outline-none !ring-0 transition-colors ${variant === "destructive"
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-primary text-primary-foreground hover:bg-primary/90"
                            } ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('dialogs.confirmation.processing')}
                            </div>
                        ) : (
                            displayConfirmText
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
} 